import React, { useState, useEffect, useContext } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	CircularProgress,
	Select,
	InputLabel,
	MenuItem,
	FormControl,
	TextField,
	InputAdornment,
	IconButton,
	FormControlLabel,
	FormHelperText,
	Switch
  } from '@material-ui/core';

import { Visibility, VisibilityOff } from '@material-ui/icons';

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import useWhatsApps from "../../hooks/useWhatsApps";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	multFieldLine: {
		display: "flex",
		"& > *:not(:last-child)": {
			marginRight: theme.spacing(1),
		},
	},

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
	formControl: {
		margin: theme.spacing(1),
		minWidth: 120,
	},
}));

const UserSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Too Short!")
		.max(50, "Too Long!")
		.required("Required"),
	password: Yup.string()
		.max(50, "Too Long!")
		.test(
			"password-min-if-provided",
			"Too Short!",
			value => !value || String(value).trim().length >= 5
		),
	email: Yup.string().email("Invalid email").required("Required"),
});

const ProfilePreferencesSchema = Yup.object().shape({
	password: Yup.string()
		.trim()
		.max(50, "Too Long!")
		.test(
			"password-min-if-provided",
			"Too Short!",
			value => !value || String(value).trim().length >= 5
		),
});

const UserModal = ({ open, onClose, userId }) => {
	const classes = useStyles();

	const { user: loggedInUser, updateCurrentUser } = useContext(AuthContext);
	const defaultProfile =
		loggedInUser?.profile === "superadmin" ? "superadmin" : "user";
	const initialState = {
		name: "",
		email: "",
		password: "",
		sipEnabled: false,
		sipUser: "",
		sipPassword: "",
		wabaCallingAllowed: false,
		supervisorMode: false,
		internalChatDeleteDirect: false,
		internalChatDeleteGroup: false,
		canSeeQueueTickets: null,
		profile: defaultProfile
	};

	const isSelfEdit = String(loggedInUser?.id) === String(userId);
	const isSuperAdminSelfEdit =
		loggedInUser?.profile === "superadmin" &&
		isSelfEdit;
	const isUserSelfPasswordOnlyEdit =
		loggedInUser?.profile === "user" &&
		isSelfEdit;
	const canManageOperationalFields =
		!isSuperAdminSelfEdit && !isUserSelfPasswordOnlyEdit;
	const canManageTenantFields =
		canManageOperationalFields && loggedInUser?.profile !== "superadmin";

	const [user, setUser] = useState(initialState);
	const [selectedQueueIds, setSelectedQueueIds] = useState([]);
	const [showPassword, setShowPassword] = useState(false);
	const [whatsappId, setWhatsappId] = useState(false);
	const {loading, whatsApps} = useWhatsApps();

	useEffect(() => {
		if (open && !userId && loggedInUser?.profile === "superadmin") {
			setUser(prevState => ({
				...prevState,
				profile: "superadmin"
			}));
		}
	}, [open, userId, loggedInUser?.profile]);

	useEffect(() => {
		const fetchUser = async () => {
			if (!userId) return;
			try {
				const { data } = await api.get(`/users/${userId}`);
				setUser(prevState => {
					return { ...prevState, ...data };
				});
				const userQueueIds = data.queues?.map(queue => queue.id);
				setSelectedQueueIds(userQueueIds);
				setWhatsappId(data.whatsappId ? data.whatsappId : '');
			} catch (err) {
				toastError(err);
			}
		};

		fetchUser();
	}, [userId, open]);

	const handleClose = () => {
		onClose();
		setUser(initialState);
	};

	const handleSaveUser = async values => {
		const normalizedPassword = String(values.password || "").trim();
		const passwordPayload = normalizedPassword
			? { password: normalizedPassword }
			: {};
		const { password, ...otherValues } = values;

		const userData = isUserSelfPasswordOnlyEdit
			? {
				...passwordPayload,
			}
			: isSuperAdminSelfEdit
			? {
				name: values.name,
				email: values.email,
				...passwordPayload,
			}
			: {
				...otherValues,
				...passwordPayload,
				whatsappId,
				queueIds: selectedQueueIds
		};
		if (!String(userData.sipPassword || "").trim()) {
			delete userData.sipPassword;
		}
		try {
			let savedUser;
			if (userId) {
				const { data } = await api.put(`/users/${userId}`, userData);
				savedUser = data;
			} else {
				const { data } = await api.post("/users", userData);
				savedUser = data;
			}
			if (isSelfEdit && savedUser && typeof updateCurrentUser === "function") {
				updateCurrentUser(savedUser);
			}
			toast.success(i18n.t("userModal.success"));
			handleClose();
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<div className={classes.root}>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth="xs"
				fullWidth
				scroll="paper"
			>
				<DialogTitle id="form-dialog-title">
					{userId
						? `${i18n.t("userModal.title.edit")} - ID #${userId}`
						: `${i18n.t("userModal.title.add")}`}
				</DialogTitle>
				<Formik
					initialValues={user}
					enableReinitialize={true}
					validationSchema={
						isUserSelfPasswordOnlyEdit ? ProfilePreferencesSchema : UserSchema
					}
					onSubmit={async (values, actions) => {
						await handleSaveUser(values);
						actions.setSubmitting(false);
					}}
				>
					{({ touched, errors, isSubmitting, values, setFieldValue }) => (
						<Form>
							<DialogContent dividers>
								<div className={classes.multFieldLine}>
									<Field
										as={TextField}
										label={i18n.t("userModal.form.name")}
										autoFocus
										name="name"
										disabled={isUserSelfPasswordOnlyEdit}
										error={touched.name && Boolean(errors.name)}
										helperText={touched.name && errors.name}
										variant="outlined"
										margin="dense"
										fullWidth
									/>
									<Field
										as={TextField}
										name="password"
										variant="outlined"
										margin="dense"
										label={i18n.t("userModal.form.password")}
										error={touched.password && Boolean(errors.password)}
										helperText={touched.password && errors.password}
										type={showPassword ? 'text' : 'password'}
										InputProps={{
										endAdornment: (
											<InputAdornment position="end">
											<IconButton
												aria-label="toggle password visibility"
												onClick={() => setShowPassword((e) => !e)}
											>
												{showPassword ? <VisibilityOff /> : <Visibility />}
											</IconButton>
											</InputAdornment>
										)
										}}
										fullWidth
									/>
								</div>
								{canManageTenantFields && (
									<>
										<FormControlLabel
											control={
												<Switch
													checked={Boolean(values.sipEnabled)}
													onChange={(event) => {
														const enabled = event.target.checked;
														setFieldValue("sipEnabled", enabled);
														if (!enabled) {
															setFieldValue("sipUser", "");
															setFieldValue("sipPassword", "");
														}
													}}
													color="primary"
												/>
											}
											label={i18n.t("userModal.form.enableSip")}
										/>
										<div className={classes.multFieldLine}>
											<Field
												as={TextField}
												label={i18n.t("userModal.form.sipUser")}
												name="sipUser"
												variant="outlined"
												margin="dense"
												disabled={!values.sipEnabled}
												fullWidth
											/>
											<Field
												as={TextField}
												label={i18n.t("userModal.form.sipPassword")}
												name="sipPassword"
												variant="outlined"
												margin="dense"
												type="password"
												disabled={!values.sipEnabled}
												fullWidth
											/>
										</div>
										<FormControlLabel
											control={
												<Switch
													checked={Boolean(values.wabaCallingAllowed)}
													onChange={(event) => {
														setFieldValue(
															"wabaCallingAllowed",
															event.target.checked
														);
													}}
													color="primary"
												/>
											}
											label={i18n.t("userModal.form.enableWabaCalling")}
										/>
										<FormHelperText>
											{i18n.t("userModal.form.enableWabaCallingHelp")}
										</FormHelperText>
									</>
								)}
								<div className={classes.multFieldLine}>
									<Field
										as={TextField}
										label={i18n.t("userModal.form.email")}
										name="email"
										disabled={isUserSelfPasswordOnlyEdit}
										error={touched.email && Boolean(errors.email)}
										helperText={touched.email && errors.email}
										variant="outlined"
										margin="dense"
										fullWidth
									/>
									<FormControl
										variant="outlined"
										className={classes.formControl}
										margin="dense"
									>
										{canManageOperationalFields && (
											<Can
												role={loggedInUser.profile}
												perform="user-modal:editProfile"
												yes={() => (
													<>
														<InputLabel id="profile-selection-input-label">
															{i18n.t("userModal.form.profile")}
														</InputLabel>

														<Field
															as={Select}
															label={i18n.t("userModal.form.profile")}
															name="profile"
															labelId="profile-selection-label"
															id="profile-selection"
															required
														>
															{loggedInUser?.profile === "superadmin" ? (
																<MenuItem value="superadmin">Superadmin</MenuItem>
															) : (
																[
																	<MenuItem key="admin" value="admin">Admin</MenuItem>,
																	<MenuItem key="user" value="user">User</MenuItem>
																]
															)}
														</Field>
													</>
												)}
											/>
										)}
									</FormControl>
								</div>
								{canManageTenantFields && (
									<>
										<FormControl margin="dense" fullWidth>
											<FormControlLabel
												control={
													<Switch
														checked={Boolean(values.supervisorMode)}
														onChange={(event) =>
															setFieldValue("supervisorMode", event.target.checked)
														}
														color="primary"
														disabled={values.profile !== "user"}
													/>
												}
												label={i18n.t("userModal.form.supervisorMode")}
											/>
											<FormHelperText>
												{i18n.t("userModal.form.supervisorModeHelp")}
											</FormHelperText>
										</FormControl>
										<FormControl margin="dense" fullWidth>
											<FormControlLabel
												control={
													<Switch
														checked={Boolean(values.internalChatDeleteDirect)}
														onChange={(event) =>
															setFieldValue(
																"internalChatDeleteDirect",
																event.target.checked
															)
														}
														color="primary"
														disabled={values.profile === "admin"}
													/>
												}
												label={i18n.t("userModal.form.internalChatDeleteDirect")}
											/>
											<FormHelperText>
												{values.profile === "admin"
													? i18n.t("userModal.form.internalChatDeleteAdminHelp")
													: i18n.t("userModal.form.internalChatDeleteDirectHelp")}
											</FormHelperText>
										</FormControl>
										<FormControl margin="dense" fullWidth>
											<FormControlLabel
												control={
													<Switch
														checked={Boolean(values.internalChatDeleteGroup)}
														onChange={(event) =>
															setFieldValue(
																"internalChatDeleteGroup",
																event.target.checked
															)
														}
														color="primary"
														disabled={values.profile === "admin"}
													/>
												}
												label={i18n.t("userModal.form.internalChatDeleteGroup")}
											/>
											<FormHelperText>
												{i18n.t("userModal.form.internalChatDeleteGroupHelp")}
											</FormHelperText>
										</FormControl>
										<FormControl
											variant="outlined"
											margin="dense"
											fullWidth
											disabled={values.profile === "admin"}
										>
											<InputLabel id="user-queue-visibility-label">
												{i18n.t("userModal.form.queueVisibility")}
											</InputLabel>
											<Select
												labelId="user-queue-visibility-label"
												label={i18n.t("userModal.form.queueVisibility")}
												value={
													values.canSeeQueueTickets === null ||
													values.canSeeQueueTickets === undefined
														? "company"
														: values.canSeeQueueTickets
														? "allow"
														: "deny"
												}
												onChange={(event) =>
													setFieldValue(
														"canSeeQueueTickets",
														event.target.value === "company"
															? null
															: event.target.value === "allow"
													)
												}
											>
												<MenuItem value="company">
													{i18n.t("userModal.form.queueVisibilityCompany")}
												</MenuItem>
												<MenuItem value="allow">
													{i18n.t("userModal.form.queueVisibilityAllow")}
												</MenuItem>
												<MenuItem value="deny">
													{i18n.t("userModal.form.queueVisibilityDeny")}
												</MenuItem>
											</Select>
											<FormHelperText>
												{values.profile === "admin"
													? i18n.t("userModal.form.queueVisibilityAdminHelp")
													: i18n.t("userModal.form.queueVisibilityHelp")}
											</FormHelperText>
										</FormControl>
										<Can
											role={loggedInUser.profile}
											perform="user-modal:editQueues"
											yes={() => (
												<QueueSelect
													selectedQueueIds={selectedQueueIds}
													onChange={values => setSelectedQueueIds(values)}
												/>
											)}
										/>
										<Can
											role={loggedInUser.profile}
											perform="user-modal:editQueues"
											yes={() => (!loading &&
												<FormControl variant="outlined" margin="dense" className={classes.maxWidth} fullWidth>
													<InputLabel>{i18n.t("userModal.form.whatsapp")}</InputLabel>
													<Field
														as={Select}
														value={whatsappId}
														onChange={(e) => setWhatsappId(e.target.value)}
														label={i18n.t("userModal.form.whatsapp")}
													>
														<MenuItem value={''}>&nbsp;</MenuItem>
														{whatsApps.map((whatsapp) => (
															<MenuItem key={whatsapp.id} value={whatsapp.id}>{whatsapp.name}</MenuItem>
														))}
													</Field>
												</FormControl>
											)}
										/>
									</>
								)}
							</DialogContent>
							<DialogActions>
								<Button
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting}
									variant="outlined"
								>
									{i18n.t("userModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting}
									variant="contained"
									className={classes.btnWrapper}
								>
									{userId
										? `${i18n.t("userModal.buttons.okEdit")}`
										: `${i18n.t("userModal.buttons.okAdd")}`}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default UserModal;
